import React, { useEffect, useState } from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import { CONTROL_INPUT, MUTED_LABEL } from './daily-entry-ui.js';
import { SCORECARD_METRICS, useDailyEntryScorecard } from './use-daily-entry-scorecard.js';
import type { MetricKey } from './use-daily-entry-scorecard.js';

interface DailyEntryScorecardProps {
  selectedDate: string;
  roster: Array<{ id: string | number; first_name?: string; last_name?: string; driver_id?: string }>;
  onRefresh: () => void;
}

export default function DailyEntryScorecard({ selectedDate, roster, onRefresh }: DailyEntryScorecardProps) {
  const [showForm, setShowForm] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const [metrics, setMetrics] = useState<Partial<Record<MetricKey, string>>>({});
  const [notes, setNotes] = useState('');

  const {
    thresholds,
    loadThresholds,
    createScorecard,
    submitting,
    error,
    lastResult,
    clearError,
    clearResult,
  } = useDailyEntryScorecard();

  useEffect(() => {
    if (showForm) loadThresholds();
  }, [showForm, loadThresholds]);

  function resetForm() {
    setEmployeeId('');
    setMetrics({});
    setNotes('');
  }

  async function handleSubmit() {
    if (!employeeId) return;

    const numericMetrics: Partial<Record<MetricKey, number | null>> = {};
    for (const m of SCORECARD_METRICS) {
      const raw = metrics[m.key];
      if (raw !== undefined && raw !== '') {
        numericMetrics[m.key] = parseFloat(raw);
      }
    }

    const success = await createScorecard(employeeId, selectedDate, numericMetrics, notes || undefined);
    if (success) {
      resetForm();
      setShowForm(false);
      onRefresh();
    }
  }

  if (!showForm) {
    return (
      <div style={{ marginBottom: 12 }}>
        <button
          type="button"
          onClick={() => { setShowForm(true); clearError(); clearResult(); }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 16px',
            borderRadius: 8,
            background: C.gold,
            color: '#111',
            border: 'none',
            fontWeight: 700,
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          Create Scorecard
        </button>

        {lastResult && (
          <div style={{ marginTop: 8, fontSize: 13, color: C.green }}>
            Scorecard created — Score: {lastResult.total_weighted_score}, Tier: {lastResult.tier || '—'}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ ...LAYOUT.subPanel, marginBottom: 12 }}>
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 10 }}>
        Create daily scorecard
      </div>

      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr', marginBottom: 12 }}>
        <div>
          <label style={MUTED_LABEL}>Driver *</label>
          <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} style={CONTROL_INPUT}>
            <option value="">Select driver...</option>
            {roster.map((d) => (
              <option key={d.id} value={d.id}>{d.first_name} {d.last_name} ({d.driver_id || d.id})</option>
            ))}
          </select>
        </div>
        <div>
          <label style={MUTED_LABEL}>Score date</label>
          <input type="date" value={selectedDate} disabled style={{ ...CONTROL_INPUT, opacity: 0.6 }} />
        </div>
      </div>

      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textMuted, marginBottom: 8 }}>
        Metrics (server calculates weighted score + tier)
      </div>

      <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', marginBottom: 12 }}>
        {SCORECARD_METRICS.map((m) => {
          const threshold = thresholds.find((t) => t.metric_key === m.key);
          return (
            <div key={m.key}>
              <label style={MUTED_LABEL}>
                {m.label}
                {threshold?.weight ? ` (${threshold.weight}%)` : ''}
              </label>
              <input
                type="number"
                step="0.01"
                value={metrics[m.key] || ''}
                onChange={(e) => setMetrics((prev) => ({ ...prev, [m.key]: e.target.value }))}
                placeholder="—"
                style={CONTROL_INPUT}
              />
            </div>
          );
        })}
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={MUTED_LABEL}>Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="Optional notes"
          style={{ ...CONTROL_INPUT, resize: 'vertical' }}
        />
      </div>

      {error && (
        <div style={{
          color: C.red, fontSize: 13, marginBottom: 10, padding: '8px 12px', borderRadius: 8,
          background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.2)',
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!employeeId || submitting}
          style={{
            padding: '10px 20px', borderRadius: 8,
            background: employeeId && !submitting ? C.gold : 'rgba(255,255,255,0.06)',
            color: employeeId && !submitting ? '#111' : 'rgba(255,255,255,0.2)',
            border: 'none', fontWeight: 700, fontSize: 13,
            cursor: employeeId && !submitting ? 'pointer' : 'not-allowed',
          }}
        >
          {submitting ? 'Creating...' : 'Create Scorecard'}
        </button>
        <button
          type="button"
          onClick={() => { setShowForm(false); clearError(); resetForm(); }}
          style={{ padding: '10px 16px', borderRadius: 8, background: 'none', color: C.textMuted, border: `1px solid ${C.borderSoft}`, fontSize: 13, cursor: 'pointer' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
